import React, { useCallback, useEffect, useState } from 'react'
import { Grid, Paper, Stack, TextField, Theme, Typography, Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import DropDownComponent, { DropDownElement } from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { UserLogin, UserLoginRequest } from '@professocialApi/professocial-core-frontend-api'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../defaults/getDateFormat'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import InfoIcon from '@mui/icons-material/Info'
import { updateUserAccountInitiated, userLoginInitiated } from '../../../store/common/common.actions'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateUserAccountRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import { CoreRootState } from '../../../store/coreRoot.reducer'
import { getLanguages, namedRequestData, namedRequestInProgress } from '../../../store/common/common.selectors'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'
import { ActionCall, Languages } from '@professocial/professocialTypes/types/commonTypes'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(3),
    padding: theme.spacing(5),
  },
}))

type Props = {
  user?: UserLogin | undefined
}

interface IUserForm {
  username?: string
  firstname?: string
  lastname?: string
  email?: string
  language?: string
  currentPassword?: string
  newPassword?: string
  repeatPassword?: string
}

const SettingsUserData: React.FC<Props> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const languages = useSelector(getLanguages)

  const [newPass, setNewPass] = useState<string | undefined>(undefined)
  const [languageCode, setLanguageCode] = useState<Languages | undefined>(Languages.de)

  const dispatchUpdateUserAccount = useCallback((params: UpdateUserAccountRequest) => dispatch(updateUserAccountInitiated(params)), [dispatch])
  const dispatchUserLogin = useCallback((login: UserLoginRequest) => dispatch(userLoginInitiated(login)), [dispatch])

  const updatingUserAccount: boolean = useSelector((state: CoreRootState) => namedRequestInProgress(state, updateUserAccountInitiated.type))
  const actionOnUpdateUserAccount: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, updateUserAccountInitiated.type))
  const validationSchema = Yup.object().shape({
    username: Yup.string(),
    firstname: Yup.string(),
    lastname: Yup.string(),
    email: Yup.string().email(`${t('accountsCommon:emailError')}`),
    currentPassword: Yup.string(),
    newPassword: Yup.string().when('currentPassword', {
      is: (currentPassword: string) => currentPassword.length > 0,
      then: Yup.string().required(`${t('accountsCommon:passwordsError')}`)
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$%\-@_]).{6,20}$/, `${t('coreSettings:descPass')}`),
    }),
    repeatPassword: Yup.string().test('passwords-match', `${t('accountsCommon:passwordsError')}`, function (value) {
      return this.parent.newPassword === value
    }),
  })

  const defaultValues: IUserForm = {
    username: props.user?.username,
    firstname: props.user?.firstName,
    lastname: props.user?.lastName,
    email: props.user?.email,
    language: props.user?.localeCode,
    currentPassword: props.user?.password,
  }

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<IUserForm>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const password = watch('currentPassword')
  const fingerprint = window.localStorage.getItem('fingerprint')

  const saveUserData: SubmitHandler<IUserForm> = (data: any) => {
    if (data && props.user) {
      const { id, username, firstName, lastName, role, enableBanner, commitmentsConfirmed, finishedWizard, finishedQuiz, nonValidate } = props.user
      const userLogin: UserLogin = {
        id,
        username: data.username || username,
        firstName: data.firstname || firstName,
        lastName: data.lastname || lastName,
        email: data.email,
        localeCode: languageCode,
        recentPassword: data.newPassword?.length === 0 ? undefined : data.currentPassword,
        password: data.newPassword?.length === 0 ? undefined : data.newPassword,
        role,
        enableBanner,
        commitmentsConfirmed,
        finishedWizard,
        finishedQuiz,
        nonValidate,
      }
      if (id && userLogin) {
        languageCode && window.localStorage.setItem('language', languageCode)
        setNewPass(userLogin.password)
        dispatchUpdateUserAccount({ userId: id, userLogin })
      }
    }
  }

  useEffect(() => {
    const status = actionOnUpdateUserAccount?.status
    if (status && status === 'success') {
      const username = getValues('username')
      if (fingerprint && newPass) {
        const loginRequest: UserLoginRequest = {
          loginCredentialsInputDto: {
            username,
            password: newPass,
            fingerprint,
          },
        }
        dispatchUserLogin(loginRequest)
      }
    }
  }, [actionOnUpdateUserAccount])

  useEffect(() => {
    reset(defaultValues)
    setLanguageCode((props.user?.localeCode || 'de') as Languages)
  }, [reset, props.user])

  return (
    <Grid
      container
      direction='row'
    >
      <Grid container item xs={12}>
        <Paper className={classes.paper}>
          <Grid container item xs={12} spacing={4}>
            <Grid item xs={12}>
              <Typography variant='h4'>{t('coreSettings:createdAt')}</Typography>
              <Typography>{props.user && props.user.createdAt && formatDate(props.user?.createdAt, getDateFormat('dateTime'))}</Typography>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='username'
                label={t('coreSettings:username')}
                type='text'
                {...register('username')}
                error={!!errors.username}
                helperText={errors.username?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='firstname'
                label={t('coreSettings:firstname')}
                type='text'
                {...register('firstname')}
                error={!!errors.firstname}
                helperText={errors.firstname?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='lastname'
                label={t('coreSettings:lastname')}
                type='text'
                {...register('lastname')}
                error={!!errors.lastname}
                helperText={errors.lastname?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='email'
                label={t('coreSettings:email')}
                type='email'
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <DropDownComponent
                label={t('coreSettings:language')}
                size='small'
                options={
                  (languages
                    ? languages.map((lang:Languages) => {
                      return { key: lang, value: t(`coreCommon:${lang.valueOf()}`) }
                    }) as DropDownElement[]
                    : Object.keys(Languages).map((lang: string) => {
                      return { key: lang, value: t(`coreCommon:${lang.valueOf()}`) }
                    }) as DropDownElement[])
                }
                value={languageCode}
                onChange={(option) => languages && setLanguageCode(languages.find((lang) => lang === option.key))}
              />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={4} mt={2}>
            <Grid item xs={12}>
              <Stack direction='row' alignItems='center'>
                <Typography sx={{ mr: 1 }} variant='h4'>{t('coreSettings:changePassword')}</Typography>
                <TooltipComponent
                  title={`${t('coreSettings:descPass')}`}
                  type='html'
                  child={<InfoIcon color='primary' />}
                  placement='right'
                  arrow
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='current_passwort'
                label={t('coreSettings:currentPassword')}
                type='password'
                {...register('currentPassword')}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                InputProps={{ autoComplete: 'new-password' }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='new_passwort'
                label={t('coreSettings:newPassword')}
                type='password'
                disabled={!password || password.length === 0}
                {...register('newPassword')}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                id='repeat_passwort'
                label={t('coreSettings:repeatPassword')}
                type='password'
                disabled={!password || password.length === 0}
                {...register('repeatPassword')}
                error={!!errors.repeatPassword}
                helperText={errors.repeatPassword?.message}
                size='small'
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 4 }}>
            <PreloaderButton
              position='right'
              loading={updatingUserAccount}
              onClick={handleSubmit(saveUserData)}
              text={`${t('facebookSettingsCommon:saveChanges')}`}
            />
          </Box>
        </Paper>
      </Grid>
    </Grid>
  )
}
export default SettingsUserData
